import playingStyles from "@/assets/js/playing-styles";
import teamInstructions from "@/assets/js/team-instructions";
import playerInstructions from "@/assets/js/player-instructions";
import PartnershipTypes from "@/assets/js/partnership-types";
import Sides from "@/assets/js/sides";
import CentralMidfieldResponsibilityPerformances from "@/assets/js/responsibilities/central-midfield";
import CentralDefenceResponsibilityPerformances from "@/assets/js/responsibilities/central-defence";
import CentralAttackResponsibilityPerformances from "@/assets/js/responsibilities/central-attack";
import FlankResponsibilityPerformances from "@/assets/js/responsibilities/flank";
import {
  IN_POSSESSION,
  OUT_OF_POSSESSION,
} from "@/assets/js/tactics";
import Partnership from "../assets/js/partnership";
import PartnershipSuitability from "@/assets/js/partnership-suitability";


export default {
  coreStyle: state => {
    return Math.max(Math.min(state.instructions.mentality, 1), -1);
  },

  attackingStyle: state => {
    return Math.max(Math.min(state.instructions.inPossession.passing, 1), -1);
  },

  defensiveStyle: state => {
    return Math.max(Math.min(state.instructions.outOfPossession.markingAndTackling.pressingUrgency, 1), -1);
  },

  playingStyleNameAddress: (state, getters) => {
    const core = ['defensive', 'balanced', 'attacking'][getters.coreStyle + 1];
    const attacking = ['shortPlays', 'mixed', 'direct'][getters.attackingStyle + 1];
    const defensive = ['cautious', 'balanced', 'aggressive'][getters.defensiveStyle + 1];
    return `tactics.tacticalStyle.${core}.${attacking}.${defensive}`;
  },

  playingStyleAnalysis: (state, getters) => {
    const inPossession = state.instructions.inPossession;
    const outOfPossession = state.instructions.outOfPossession;
    const instructionStates = {
      mentality: state.instructions.mentality,
      attackingWidth: inPossession.attackingWidth.attackingWidth,
      passIntoSpace: inPossession.approachPlay.passIntoSpace,
      playOutOfDefence: inPossession.approachPlay.playOutOfDefence,
      passing: inPossession.passing,
      tempo: inPossession.tempo,
      timeWasting: inPossession.timeWasting,
      hitEarlyCrosses: inPossession.finalThird.hitEarlyCrosses,
      shootOnSight: inPossession.finalThird.shootOnSight,
      workBallIntoBox: inPossession.finalThird.workBallIntoBox,
      playForSetPieces: inPossession.finalThird.playForSetPieces,
      dribbling: inPossession.dribbling,
      creativeFreedom: inPossession.creativeFreedom,
      useOffsideTrap: outOfPossession.defensiveShape.useOffsideTrap,
      engagementLine: outOfPossession.defensiveShape.engagementLine,
      defensiveLine: outOfPossession.defensiveShape.defensiveLine,
      defensiveWidth: outOfPossession.defensiveShape.defensiveWidth,
      tighterMarking: outOfPossession.markingAndTackling.useTighterMarking,
      pressing: outOfPossession.markingAndTackling.pressingUrgency,
      preventGKDistribution: outOfPossession.markingAndTackling.preventGKDistribution,
      tackling: outOfPossession.markingAndTackling.tackling,
      pressingTrap: outOfPossession.pressingTrap,
      crossEngagement: outOfPossession.crossEngagement,
      counterPress: state.instructions.inTransition.defensiveTransition,
      counter: state.instructions.inTransition.attackingTransition,
      goalkeeperInPossession: state.instructions.inTransition.goalkeeperInPossession,
    };
    let netHighRiskScore = 0;
    let netLowRiskScore = 0;
    let netInPossessionScore = 0;
    let netOutOfPossessionScore = 0;

    Object.values(playingStyles).forEach(styleMethod => {
      styleMethod.highRiskScore = 0;
      styleMethod.lowRiskScore = 0;
    });

    for (const [instruction, instructionState] of Object.entries(instructionStates)) {
      const tiProfileIndex = instructionState + teamInstructions[instruction].offset;
      addInstructionRiskProfileContribution(playingStyles, Object.entries(teamInstructions[instruction].risk.profiles[tiProfileIndex]))
    }

    for (const position of getters.positions()) {
      for (const [piKey, piData] of Object.entries(playerInstructions)) {
        const profileIndex = position.playerInstructions[piKey] + piData.offset;
        addInstructionRiskProfileContribution(playingStyles, Object.entries(piData.risk.profiles[profileIndex]))
      }
    } 

    Object.values(playingStyles).forEach(styleMethod => {
      styleMethod.netRiskScore = styleMethod.highRiskScore - styleMethod.lowRiskScore;
      netHighRiskScore += styleMethod.highRiskScore;
      netLowRiskScore += styleMethod.lowRiskScore;
      if (styleMethod.possessionType !== OUT_OF_POSSESSION) netInPossessionScore += styleMethod.netRiskScore;
      if (styleMethod.possessionType !== IN_POSSESSION) netOutOfPossessionScore += styleMethod.netRiskScore;
    });

    return {
      netHighRiskScore,
      netLowRiskScore,
      netRiskScore: netHighRiskScore - netLowRiskScore,
      netInPossessionScore,
      netOutOfPossessionScore,
      breakdown: playingStyles
    };
  },

  forwardMovementScores (state, getters) {
    const allPositions = getters.positions();
    const leftPositions = getters.positions(Sides.left);
    const rightPositions = getters.positions(Sides.right);

    return {
      penetration: {
        score: penetrationScore(allPositions),
        left: penetrationScore(leftPositions, Sides.left),
        right: penetrationScore(rightPositions, Sides.right),
        target: 9.5
      },
      solidity: {
        score: solidityScore(allPositions),
        left: solidityScore(leftPositions, Sides.left),
        right: solidityScore(rightPositions, Sides.right),
        target: 15.5
      },
      support: {
        score: supportScore(allPositions),
        left: supportScore(leftPositions, Sides.left),
        right: supportScore(rightPositions, Sides.right),
        target: 12.5 + getters.attackingStyle * (-1)
      }
    };
  },

  partnerships(state, getters) {
    const positions = getters.positions().filter(position => !position.goalkeeper);
    if (!positions.length) return {};

    const positionPartnerability = positions.map(position => {
      return {
        positionNumber: position.positionNumber,
        side: position.side,
        centralAttack: position.centralAttack.partnerability,
        centralMidfield: position.centralMidfield.partnerability,
        flanks: position.flanks.partnerability,
      };
    });

    const partnerships = {
      centralAttack: [],
      centralMidfield: [],
      leftFlank: [],
      rightFlank: [],
    };

    for (const zone of ["centralAttack", "centralMidfield"]) {
      for (let i = 0; i < 9; i++) {
        for (let [partnershipType, partner] of Object.entries(positionPartnerability[i][zone])) {
          if (!partner) continue;

          for (let j = i+1; j < 10; j++) {
            const comparisonPartner = positionPartnerability[j][zone][partnershipType];
            const partnershipCategory = validPartnership(partner, comparisonPartner);
            if (!partnershipCategory) continue;

            partnerships[zone] = partnerships[zone] || [];
            const partnership = new Partnership(
              partnershipType,
              partnershipCategory,
              positionPartnerability[i].positionNumber,
              positionPartnerability[j].positionNumber,
              PartnershipSuitability[zone][partnershipType]
            )
            partnerships[zone].push(partnership);
          }
        }
      }
    }
    
    for (let side of [Sides.left, Sides.right]) {
      let zone = side.toLowerCase() + "Flank";
      let sidePartnerability = positionPartnerability.filter(position => position.side === side);
      for (let i = 0, c = sidePartnerability.length; i < c; i++) {
        for (let [partnershipType, partner] of Object.entries(sidePartnerability[i].flanks)) {
          if (!partner) continue;

          for (let j = i+1; j < c; j++) {
            const comparisonPartner = sidePartnerability[j].flanks[partnershipType];
            const partnershipCategory = validPartnership(partner, comparisonPartner);
            if (!partnershipCategory) continue;

            partnerships[zone] = partnerships[zone] || [];
            const partnership = new Partnership(
              partnershipType,
              partnershipCategory,
              sidePartnerability[i].positionNumber,
              sidePartnerability[j].positionNumber,
              PartnershipSuitability.flanks[partnershipType]
            )
            partnerships[zone].push(partnership);
          }
        }
      }
    }

    return partnerships;
  },

  partnershipCounts(state, getters) {
    let counts = { total: { direct: 0, overlapping: 0 } };
    for (const [zone, partnerships] of Object.entries(getters.partnerships)) {
      counts[zone] = {};
      for (const partnership of partnerships) {
        counts[zone][partnership.partnershipType] = counts[zone][partnership.partnershipType] || {};
        const count = counts[zone][partnership.partnershipType][partnership.partnershipCategory] || 0;
        counts[zone][partnership.partnershipType][partnership.partnershipCategory] = count + 1;

        if (partnership.partnershipType.toLowerCase().includes(PartnershipTypes.direct)) {
          counts.total.direct++;
        }
        if (partnership.partnershipType.toLowerCase().includes(PartnershipTypes.overlapping)) {
          counts.total.overlapping++;
        }
      }
    }
    return counts;
  },

  positionalResponsibilities(state, getters) {
    const positions = getters.positions();
    if (!positions.length) return {};

    const performances = {
      centralAttack: new CentralAttackResponsibilityPerformances(),
      centralMidfield: new CentralMidfieldResponsibilityPerformances(),
      leftFlank: new FlankResponsibilityPerformances(),
      rightFlank: new FlankResponsibilityPerformances(),
      centralDefence: new CentralDefenceResponsibilityPerformances(),
    };
    
    for (const position of positions) {
      performances.centralAttack.countPerformance(position.centralAttack.responsibilityPerformance);
      performances.centralMidfield.countPerformance(position.centralMidfield.responsibilityPerformance);
      performances.centralDefence.countPerformance(position.centralDefence.responsibilityPerformance);

      const flankPerformances = position.flanks.responsibilityPerformance;
      if (position.side === Sides.left) {
        performances.leftFlank.countPerformance(flankPerformances);
      }
      if (position.side === Sides.right) {
        performances.rightFlank.countPerformance(flankPerformances);
      }
    }
    
    return performances;
  },
};


const validPartnership = (partner, comparisonPartner) => {
  if (!comparisonPartner) return false;
  if (partner.pairWith.partnerType !== comparisonPartner.type) return false;
  if (comparisonPartner.stratum.height < partner.pairWith.strata.min.height) return false;
  if (comparisonPartner.stratum.height > partner.pairWith.strata.max.height) return false;
  if (comparisonPartner.pairWith.partnerType !== partner.type) return false;
  if (partner.stratum.height < comparisonPartner.pairWith.strata.min.height) return false;
  if (partner.stratum.height > comparisonPartner.pairWith.strata.max.height) return false;

  return partner.type !== comparisonPartner.type?
    PartnershipTypes.imbalanced:
    partner.type;
}


const penetrationScore = (positions, side = null) => {
  return positions.reduce((total, position) => {
    return total + (position.side === side ? 2 : 1) * position.penetrationScore;
  }, 0);
}


const solidityScore = (positions, side = null) => {
  return positions.reduce((total, position) => {
    return total + (position.side === side ? 2 : 1) * position.solidityScore;
  }, 0);
}


const supportScore = (positions, side=null) => {
  return positions.reduce((total, position) => {
    return total + (position.side === side ? 2 : 1) * position.supportScore;
  }, 0);
}


const addInstructionRiskProfileContribution = (playingStyles, riskProfile) => {
  for (const [styleMethodKey, score] of riskProfile) {
    const styleMethod = playingStyles[styleMethodKey];
    styleMethod.highRiskScore += score.high;
    styleMethod.lowRiskScore += score.low;
  }
}
